import { useEffect, useRef, useState } from "react";
import { api, fmtDate, currency } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Mail, Paperclip, Loader2, Bold, Italic, Underline, Link } from "lucide-react";

/** Replace {{var}} placeholders in a template string */
function tpl(str, vars) {
  if (!str) return "";
  return Object.entries(vars).reduce(
    (s, [k, v]) => s.replaceAll(`{{${k}}}`, v ?? ""),
    str
  );
}

export default function EmailPreviewModal({
  open, onClose,
  title,
  toEmail: initialEmail = "",
  customerEmail,
  docType = "invoice",
  invoice,
  attachmentName,
  extraVars = {},
  onSend,
  sending = false,
}) {
  const [toEmail, setToEmail] = useState(initialEmail);
  const [subject, setSubject] = useState("");
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const editorRef = useRef(null);

  const buildVars = (companyName) => ({
    customer_name: invoice?.customer_name || "",
    invoice_number: invoice?.invoice_number || "",
    invoice_date: fmtDate(invoice?.invoice_date),
    due_date: fmtDate(invoice?.due_date),
    total: invoice?.total != null ? currency(invoice.total) : "",
    company_name: companyName || "J&L Industrial Supplies",
    order_number: invoice?.order_number || "",
    signed_by: invoice?.signature_data?.signed_by || "",
    signed_date: fmtDate(invoice?.signature_data?.signed_at),
    ...extraVars,
  });

  useEffect(() => {
    if (!open) return;
    setToEmail(initialEmail);
    setLoadingTemplate(true);

    Promise.all([
      api.get("/email-templates"),
      api.get("/settings"),
    ]).then(([tmplRes, settingsRes]) => {
      const templates = tmplRes.data;
      const co = settingsRes.data?.company_details || {};
      const companyName = co.name || "J&L Industrial Supplies";
      const vars = buildVars(companyName);

      const tmpl = templates[docType] || {};
      setSubject(tpl(tmpl.subject || "", vars));

      const rendered = tpl(tmpl.body_html || "", vars).replace(
        "{{invoice_table}}",
        "<p style='color:#888;font-size:12px;font-style:italic;'>[Invoice line items — see attached PDF]</p>"
      );
      // Set editor content imperatively — avoids React re-render resetting edits
      if (editorRef.current) editorRef.current.innerHTML = rendered;
    }).catch(() => {
      setSubject(`Email — ${invoice?.invoice_number || ""}`);
      if (editorRef.current) editorRef.current.innerHTML = "<p>Please find the document attached.</p>";
    }).finally(() => setLoadingTemplate(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, docType, invoice?.id]);

  // Also set innerHTML when ref mounts (handles the case where ref attaches after effect runs)
  const editorCallbackRef = (node) => {
    editorRef.current = node;
  };

  const execFormat = (cmd, value = null) => {
    document.execCommand(cmd, false, value);
    editorRef.current?.focus();
  };

  const insertLink = () => {
    const url = window.prompt("Enter URL:", "https://");
    if (url) execFormat("createLink", url);
  };

  const handleSend = () => {
    if (!toEmail.trim()) return;
    const bodyHtml = editorRef.current?.innerHTML || "";
    onSend({ toEmail: toEmail.trim(), subject, customBodyHtml: bodyHtml });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!sending && !o) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[95vh] overflow-y-auto" data-testid="email-preview-modal">
        <DialogTitle className="text-[#032352] font-heading font-bold flex items-center gap-2">
          <Mail className="w-5 h-5" /> {title}
        </DialogTitle>
        <DialogDescription className="sr-only">Email preview and compose</DialogDescription>

        <div className="space-y-4 pt-1">
          {/* To */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5B5C5F]">To</Label>
            <Input
              data-testid="email-modal-to"
              type="email"
              value={toEmail}
              onChange={(e) => setToEmail(e.target.value)}
              className="mt-1"
              placeholder="customer@example.com"
            />
            {customerEmail && toEmail !== customerEmail && (
              <button
                className="text-xs text-[#032352] underline mt-1"
                onClick={() => setToEmail(customerEmail)}
                data-testid="email-modal-use-account-email"
              >
                Use account email: {customerEmail}
              </button>
            )}
          </div>

          {/* Subject */}
          <div>
            <Label className="text-xs uppercase tracking-wider text-[#5B5C5F]">Subject</Label>
            <Input
              data-testid="email-modal-subject"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1"
              placeholder="Email subject…"
            />
          </div>

          {/* Editable body */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs uppercase tracking-wider text-[#5B5C5F]">Email Body</Label>
              {loadingTemplate && (
                <span className="flex items-center gap-1 text-xs text-[#5B5C5F]">
                  <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                </span>
              )}
            </div>

            {/* Formatting toolbar */}
            <div className="flex items-center gap-1 border border-slate-200 border-b-0 rounded-t-lg bg-[#F8FAFC] px-2 py-1.5">
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); execFormat("bold"); }}
                className="p-1.5 rounded hover:bg-slate-200 transition-colors text-[#032352]"
                title="Bold"
                data-testid="format-bold"
              >
                <Bold className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); execFormat("italic"); }}
                className="p-1.5 rounded hover:bg-slate-200 transition-colors text-[#032352]"
                title="Italic"
                data-testid="format-italic"
              >
                <Italic className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); execFormat("underline"); }}
                className="p-1.5 rounded hover:bg-slate-200 transition-colors text-[#032352]"
                title="Underline"
                data-testid="format-underline"
              >
                <Underline className="w-3.5 h-3.5" />
              </button>
              <div className="w-px h-4 bg-slate-300 mx-1" />
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); insertLink(); }}
                className="p-1.5 rounded hover:bg-slate-200 transition-colors text-[#032352]"
                title="Insert link"
                data-testid="format-link"
              >
                <Link className="w-3.5 h-3.5" />
              </button>
              <div className="ml-auto text-[10px] text-slate-400">Click to edit</div>
            </div>

            {/* ContentEditable editor */}
            <div
              ref={editorCallbackRef}
              contentEditable
              suppressContentEditableWarning
              data-testid="email-modal-editor"
              className="border border-slate-200 rounded-b-lg bg-white overflow-y-auto focus:outline-none focus:ring-2 focus:ring-[#032352]/20"
              style={{
                minHeight: 220,
                maxHeight: 340,
                padding: "16px 20px",
                fontSize: 14,
                lineHeight: 1.6,
                color: "#333",
              }}
            />
          </div>

          {/* Attachment badge */}
          {attachmentName && (
            <div className="flex items-center gap-2 text-xs text-[#5B5C5F] bg-slate-50 rounded-md px-3 py-2 border border-slate-200">
              <Paperclip className="w-3.5 h-3.5 shrink-0" />
              <span>{attachmentName} — attached as PDF</span>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2 justify-end pt-2">
            <Button variant="outline" onClick={onClose} disabled={sending} data-testid="email-modal-cancel">
              Cancel
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending || !toEmail.trim() || loadingTemplate}
              data-testid="email-modal-send"
              className="bg-[#032352] hover:bg-[#032352]/90"
            >
              {sending
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                : <><Mail className="w-4 h-4 mr-2" /> Send Email</>
              }
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
